import React, { useState, useEffect, useRef } from 'react';
import { adminAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { useSettings } from '../../contexts/SettingsContext';
import { 
  Phone, Mail, Image, Upload, Loader2, 
  Facebook, Instagram, Twitter, Youtube, Palette
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Color presets for quick theme switching
const COLOR_PRESETS = [
  { name: 'Default Green', home_bg: '#ffffff', tours_bg: '#f9fafb', hero_bg: '#166534', primary: '#166534', secondary: '#f59e0b', description: 'Classic nature-inspired theme' },
  { name: 'Ocean Blue', home_bg: '#f0f9ff', tours_bg: '#e0f2fe', hero_bg: '#0369a1', primary: '#0284c7', secondary: '#06b6d4', description: 'Fresh coastal vibes' },
  { name: 'Sunset Orange', home_bg: '#fffbeb', tours_bg: '#fef3c7', hero_bg: '#b45309', primary: '#d97706', secondary: '#ea580c', description: 'Warm tropical sunset' },
  { name: 'Royal Purple', home_bg: '#faf5ff', tours_bg: '#f3e8ff', hero_bg: '#6b21a8', primary: '#7c3aed', secondary: '#ec4899', description: 'Elegant and luxurious' },
  { name: 'Forest Green', home_bg: '#f0fdf4', tours_bg: '#dcfce7', hero_bg: '#14532d', primary: '#15803d', secondary: '#eab308', description: 'Earthy rainforest theme' },
  { name: 'Coral Reef', home_bg: '#fff1f2', tours_bg: '#ffe4e6', hero_bg: '#be123c', primary: '#e11d48', secondary: '#fb923c', description: 'Vibrant and energetic' },
  { name: 'Midnight', home_bg: '#0f172a', tours_bg: '#1e293b', hero_bg: '#334155', primary: '#38bdf8', secondary: '#a78bfa', description: 'Dark mode elegance' },
  { name: 'Minimal White', home_bg: '#ffffff', tours_bg: '#ffffff', hero_bg: '#0f172a', primary: '#0f172a', secondary: '#64748b', description: 'Clean and professional' },
  { name: 'Caribbean', home_bg: '#ecfeff', tours_bg: '#cffafe', hero_bg: '#0e7490', primary: '#0891b2', secondary: '#fbbf24', description: 'Tropical Caribbean waters' },
  { name: 'Volcano', home_bg: '#fef2f2', tours_bg: '#fee2e2', hero_bg: '#7f1d1d', primary: '#dc2626', secondary: '#f97316', description: 'Bold and adventurous' },
];

// Typography presets
const TYPOGRAPHY_PRESETS = [
  { name: 'Default', font: '', size: 'lg' },
  { name: 'Bold', font: 'font-bold', size: 'xl' },
  { name: 'Elegant', font: 'font-serif', size: 'lg' },
  { name: 'Compact', font: '', size: 'base' },
];

export default function SettingsTab({ settings, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const { refreshSettings } = useSettings(); // Get global refresh function
  const [formData, setFormData] = useState({
    phone_number: '',
    whatsapp_number: '',
    email: '',
    address: '',
    facebook_url: '',
    instagram_url: '',
    twitter_url: '',
    youtube_url: '',
    tiktok_url: '',
    site_logo_url: '',
    site_name: 'Tour Finder',  // NEW: Site name
    logo_text: 'Tour Finder',  // NEW: Logo text (fallback when no logo image)
    // Site Colors
    home_bg_color: '#ffffff',
    tours_bg_color: '#f9fafb',
    hero_bg_color: '#166534',
    primary_color: '#166534',
    secondary_color: '#f59e0b',
    // Typography
    card_title_font: '',
    heading_font: '',
    body_font: '',
    card_title_size: 'lg',
  });

  useEffect(() => {
    if (settings) {
      setFormData({
        phone_number: settings.phone_number || '',
        whatsapp_number: settings.whatsapp_number || '',
        email: settings.email || '',
        address: settings.address || '',
        facebook_url: settings.facebook_url || '',
        instagram_url: settings.instagram_url || '',
        twitter_url: settings.twitter_url || '',
        youtube_url: settings.youtube_url || '',
        tiktok_url: settings.tiktok_url || '',
        site_logo_url: settings.site_logo_url || '',
        site_name: settings.site_name || 'Tour Finder',
        logo_text: settings.logo_text || 'Tour Finder',
        // Site Colors
        home_bg_color: settings.home_bg_color || '#ffffff',
        tours_bg_color: settings.tours_bg_color || '#f9fafb',
        hero_bg_color: settings.hero_bg_color || '#166534',
        primary_color: settings.primary_color || '#166534',
        secondary_color: settings.secondary_color || '#f59e0b',
        // Typography
        card_title_font: settings.card_title_font || '',
        heading_font: settings.heading_font || '',
        body_font: settings.body_font || '',
        card_title_size: settings.card_title_size || 'lg',
      });
    }
  }, [settings]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      // Use FormData for file upload
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('logo_type', 'main');
      
      const response = await fetch(`${API_URL}/api/admin/branding/upload-logo?logo_type=main`, {
        method: 'POST',
        credentials: 'include',
        body: uploadData,
      });
      
      if (!response.ok) {
        // Fallback to base64 upload
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = reader.result;
          const fallbackResponse = await fetch(`${API_URL}/api/upload/image`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ image_data: base64, category: 'logos' }),
          });
          
          if (!fallbackResponse.ok) throw new Error('Upload failed');
          
          const data = await fallbackResponse.json();
          const logoUrl = data.url.startsWith('/api') ? `${API_URL}${data.url}` : `${API_URL}/api${data.url}`;
          
          setFormData(prev => ({ ...prev, site_logo_url: logoUrl }));
          toast.success('Logo uploaded! Click Save Settings to apply.');
          setUploadingLogo(false);
        };
        reader.onerror = () => {
          toast.error('Failed to read file');
          setUploadingLogo(false);
        };
        reader.readAsDataURL(file);
        return;
      }
      
      const data = await response.json();
      const logoUrl = data.url.startsWith('/api') ? `${API_URL}${data.url}` : `${API_URL}/api${data.url}`;
      
      setFormData(prev => ({ ...prev, site_logo_url: logoUrl }));
      toast.success('Logo uploaded to cloud storage! Click Save Settings to apply.');
      setUploadingLogo(false);
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await adminAPI.updateSettings(formData);
      toast.success('Settings saved successfully!');
      onRefresh(); // Refresh admin dashboard data
      refreshSettings(); // Refresh global settings context (Footer, FloatingCallButton, etc.)
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="settings-tab">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold">Site Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your contact information and social media links</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={loading}
          className="rounded-full bg-primary"
          data-testid="save-settings-btn"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            'Save Settings'
          )}
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Site Branding */}
        <Card className="p-6 rounded-xl">
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
            <Image className="h-5 w-5 text-primary" />
            Site Branding
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Customize your site logo and branding. Changes will appear on the header across the site.
          </p>
          
          {/* Site Name & Logo Text */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label htmlFor="site_name" className="text-sm font-medium">Site Name</Label>
              <p className="text-xs text-muted-foreground mb-1">Used for page titles and SEO</p>
              <Input
                id="site_name"
                value={formData.site_name}
                onChange={(e) => setFormData({ ...formData, site_name: e.target.value })}
                placeholder="Tour Finder"
                data-testid="site-name-input"
              />
            </div>
            <div>
              <Label htmlFor="logo_text" className="text-sm font-medium">Logo Text</Label>
              <p className="text-xs text-muted-foreground mb-1">Text shown next to logo or when no logo image</p>
              <Input
                id="logo_text"
                value={formData.logo_text}
                onChange={(e) => setFormData({ ...formData, logo_text: e.target.value })}
                placeholder="Tour Finder"
                data-testid="logo-text-input"
              />
            </div>
          </div>
          
          {/* Logo Upload */}
          <div className="flex items-center gap-6">
            {/* Logo Preview */}
            <div className="w-24 h-24 rounded-xl border-2 border-dashed border-border flex items-center justify-center bg-muted/30 overflow-hidden">
              {formData.site_logo_url ? (
                <img 
                  src={formData.site_logo_url} 
                  alt="Site Logo" 
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-center">
                  <Image className="h-8 w-8 text-muted-foreground mx-auto" />
                  <p className="text-xs text-muted-foreground mt-1">No logo</p>
                </div>
              )}
            </div>
            
            {/* Upload Controls */}
            <div className="flex flex-col gap-2">
              <input
                type="file"
                ref={logoInputRef}
                onChange={handleLogoUpload}
                accept="image/*"
                className="hidden"
                data-testid="logo-file-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                className="gap-2"
                data-testid="upload-logo-btn"
              >
                {uploadingLogo ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Upload Logo Image
                  </>
                )}
              </Button>
              {formData.site_logo_url && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFormData({ ...formData, site_logo_url: '' })}
                  className="text-red-600 hover:text-red-700"
                >
                  Remove Logo
                </Button>
              )}
              <p className="text-xs text-muted-foreground">
                Recommended: 200x200px. Max 5MB. PNG, JPG, WebP.
              </p>
            </div>
          </div>
        </Card>

        {/* Contact Information */}
        <Card className="p-6 rounded-xl">
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
            <Phone className="h-5 w-5 text-primary" />
            Contact Information
          </h2>
          <div className="grid gap-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone_number" className="text-sm font-medium">Phone Number</Label>
                <p className="text-xs text-muted-foreground mb-1">Displayed on the Call Agent button</p>
                <Input
                  id="phone_number"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  placeholder="+1 (787) 555-0123"
                  data-testid="phone-number-input"
                />
              </div>
              <div>
                <Label htmlFor="whatsapp_number" className="text-sm font-medium">WhatsApp Number</Label>
                <p className="text-xs text-muted-foreground mb-1">Numbers only, with country code (e.g., 17875550123)</p>
                <Input
                  id="whatsapp_number"
                  value={formData.whatsapp_number}
                  onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                  placeholder="17875550123"
                  data-testid="whatsapp-number-input"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="hello@tourfinder.pr"
                  data-testid="email-input"
                />
              </div>
              <div>
                <Label htmlFor="address" className="text-sm font-medium">Business Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Calle del Sol, San Juan, PR 00901"
                  data-testid="address-input"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Social Media Links */}
        <Card className="p-6 rounded-xl">
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
            <Instagram className="h-5 w-5 text-primary" />
            Social Media Links
          </h2>
          <div className="grid gap-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="facebook_url" className="text-sm font-medium flex items-center gap-2">
                  <Facebook className="h-4 w-4 text-blue-600" />
                  Facebook URL
                </Label>
                <Input
                  id="facebook_url"
                  value={formData.facebook_url}
                  onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
                  placeholder="https://facebook.com/yourpage"
                  data-testid="facebook-url-input"
                />
              </div>
              <div>
                <Label htmlFor="instagram_url" className="text-sm font-medium flex items-center gap-2">
                  <Instagram className="h-4 w-4 text-pink-600" />
                  Instagram URL
                </Label>
                <Input
                  id="instagram_url"
                  value={formData.instagram_url}
                  onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                  placeholder="https://instagram.com/yourprofile"
                  data-testid="instagram-url-input"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="twitter_url" className="text-sm font-medium flex items-center gap-2">
                  <Twitter className="h-4 w-4 text-blue-400" />
                  Twitter / X URL
                </Label>
                <Input
                  id="twitter_url"
                  value={formData.twitter_url}
                  onChange={(e) => setFormData({ ...formData, twitter_url: e.target.value })}
                  placeholder="https://twitter.com/yourhandle"
                  data-testid="twitter-url-input"
                />
              </div>
              <div>
                <Label htmlFor="youtube_url" className="text-sm font-medium flex items-center gap-2">
                  <Youtube className="h-4 w-4 text-red-600" />
                  YouTube URL
                </Label>
                <Input
                  id="youtube_url"
                  value={formData.youtube_url}
                  onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                  placeholder="https://youtube.com/@yourchannel"
                  data-testid="youtube-url-input"
                />
              </div>
            </div>
            <div className="md:w-1/2">
              <Label htmlFor="tiktok_url" className="text-sm font-medium flex items-center gap-2">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
                TikTok URL
              </Label>
              <Input
                id="tiktok_url"
                value={formData.tiktok_url}
                onChange={(e) => setFormData({ ...formData, tiktok_url: e.target.value })}
                placeholder="https://tiktok.com/@yourprofile"
                data-testid="tiktok-url-input"
              />
            </div>
          </div>
        </Card>

        {/* Site Colors */}
        <Card className="p-6 rounded-xl">
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
            <Palette className="h-5 w-5 text-primary" />
            Site Colors & Themes
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Customize the colors used throughout your website. Changes apply to the homepage, hero section, buttons, and badges.
          </p>
          
          {/* Theme Presets - Grid Layout */}
          <div className="mb-6">
            <Label className="text-sm font-medium mb-3 block">Theme Presets</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {COLOR_PRESETS.map((preset) => {
                const isSelected = 
                  formData.primary_color === preset.primary && 
                  formData.secondary_color === preset.secondary;
                return (
                  <button
                    key={preset.name}
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      home_bg_color: preset.home_bg,
                      tours_bg_color: preset.tours_bg,
                      hero_bg_color: preset.hero_bg,
                      primary_color: preset.primary,
                      secondary_color: preset.secondary,
                    }))}
                    className={`p-3 rounded-xl border-2 transition-all text-left ${
                      isSelected 
                        ? 'border-primary ring-2 ring-primary/20' 
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    {/* Color Preview */}
                    <div className="flex gap-1 mb-2">
                      <div 
                        className="w-8 h-8 rounded-lg shadow-sm" 
                        style={{ backgroundColor: preset.hero_bg }}
                        title="Hero Background"
                      />
                      <div 
                        className="w-8 h-8 rounded-lg shadow-sm" 
                        style={{ backgroundColor: preset.primary }}
                        title="Primary Color"
                      />
                      <div 
                        className="w-8 h-8 rounded-lg shadow-sm" 
                        style={{ backgroundColor: preset.secondary }}
                        title="Secondary Color"
                      />
                    </div>
                    <p className="text-xs font-semibold truncate">{preset.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{preset.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Live Preview */}
          <div className="mb-6 p-4 rounded-xl border-2 border-dashed border-border">
            <Label className="text-sm font-medium mb-3 block">Live Preview</Label>
            <div className="rounded-lg overflow-hidden shadow-lg">
              {/* Mini Hero Preview */}
              <div 
                className="h-16 flex items-center justify-center text-white text-sm font-semibold"
                style={{ backgroundColor: formData.hero_bg_color }}
              >
                Hero Section
              </div>
              {/* Mini Content Preview */}
              <div 
                className="p-4"
                style={{ backgroundColor: formData.home_bg_color }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div 
                    className="px-3 py-1 rounded-full text-white text-xs"
                    style={{ backgroundColor: formData.primary_color }}
                  >
                    Primary Button
                  </div>
                  <div 
                    className="px-3 py-1 rounded-full text-white text-xs"
                    style={{ backgroundColor: formData.secondary_color }}
                  >
                    Secondary
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  Page Background: {formData.home_bg_color}
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-medium">Homepage Background</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={formData.home_bg_color}
                    onChange={(e) => setFormData({ ...formData, home_bg_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border-0"
                  />
                  <Input
                    value={formData.home_bg_color}
                    onChange={(e) => setFormData({ ...formData, home_bg_color: e.target.value })}
                    className="font-mono text-sm"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Tours Page Background</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={formData.tours_bg_color}
                    onChange={(e) => setFormData({ ...formData, tours_bg_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border-0"
                  />
                  <Input
                    value={formData.tours_bg_color}
                    onChange={(e) => setFormData({ ...formData, tours_bg_color: e.target.value })}
                    className="font-mono text-sm"
                    placeholder="#f9fafb"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Hero Section Background</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={formData.hero_bg_color}
                    onChange={(e) => setFormData({ ...formData, hero_bg_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border-0"
                  />
                  <Input
                    value={formData.hero_bg_color}
                    onChange={(e) => setFormData({ ...formData, hero_bg_color: e.target.value })}
                    className="font-mono text-sm"
                    placeholder="#166534"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Primary Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border-0"
                  />
                  <Input
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="font-mono text-sm"
                    placeholder="#166534"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Secondary Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border-0"
                  />
                  <Input
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                    className="font-mono text-sm"
                    placeholder="#f59e0b"
                  />
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Typography Settings */}
        <Card className="p-6 rounded-xl">
          <h2 className="font-heading text-xl font-semibold mb-4">Typography</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Customize fonts and text sizes for tour cards
          </p>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Card Title Style Presets</Label>
              <div className="flex flex-wrap gap-2">
                {TYPOGRAPHY_PRESETS.map((preset) => (
                  <Button
                    key={preset.name}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      card_title_font: preset.font,
                      card_title_size: preset.size,
                    }))}
                  >
                    {preset.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Info Box */}
        <div className="p-4 bg-blue-50 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">How these settings are used:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Phone Number</strong> - Used for the "Call Now" button on your site</li>
            <li>• <strong>WhatsApp Number</strong> - Used for the "WhatsApp" button (include country code without +)</li>
            <li>• <strong>Social Media Links</strong> - Displayed in the footer of your website</li>
            <li>• <strong>Urgency Settings</strong> - Control urgency badges per-tour in Manage Tours → Edit Tour → Urgency tab</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
